<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useIndexStore } from '@/store'
import logo from '@/assets/imgs/logo.png'
import languageIcon from '@/assets/svg/lanuage.svg'
import { formatEthereumAddress } from '@/utils'
import LangPopup from '@/components/lang.vue'

defineProps({
  theme: { type: String, default: 'dark' },
  showTips: { type: Boolean, default: false }
})

const emit = defineEmits(['connectWallet'])
const router = useRouter()
const route = useRoute()
const store = useIndexStore()
const { t } = useI18n()
const langRef = ref(null)

const mainPages = ['/index', '/power', '/invite', '/mine']
const isInnerPage = computed(() => !mainPages.includes(route.path))
const walletLabel = computed(() =>
  store.address ? formatEthereumAddress(store.address) : t('header.connectWallet')
)

const goBack = () => {
  if (window.history.length > 1) router.back()
  else router.push('/index')
}
</script>

<template>
  <header class="app-header">
    <nav class="header-inner" aria-label="应用顶部导航">
      <button v-if="isInnerPage" class="icon-button" :aria-label="t('header.back') || '返回'" @click="goBack">
        <van-icon name="arrow-left" />
      </button>
      <button v-else class="brand" :aria-label="t('header.home') || '首页'" @click="router.push('/index')">
        <span class="brand-mark"><img :src="logo" alt="" /></span>
        <span><strong>XZEC</strong><small>USDT YIELD</small></span>
      </button>

      <div class="header-actions">
        <button class="lang-button" :aria-label="t('langList.selectLanguage')" @click="langRef?.open()">
          <img :src="languageIcon" alt="" />
        </button>
        <button class="wallet-button" :class="{ connected: store.address }"
          @click="!store.address && emit('connectWallet')">
          <i></i>
          {{ walletLabel }}
        </button>
      </div>
    </nav>
  </header>

  <LangPopup ref="langRef" />
</template>

<style scoped lang="scss">
.app-header {
  position: relative;
  z-index: 100;
  width: 100%;
  padding-top: env(safe-area-inset-top);
  color: #f0e5d2;
  background: rgba(18, 49, 50, .94);
  border-bottom: 1px solid rgba(196, 222, 207, .16);
  backdrop-filter: blur(1rem);
}

.header-inner {
  width: min(100%, 32.5rem);
  min-height: 3.5rem;
  margin: 0 auto;
  padding: 0 .875rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .5rem;
}

button {
  min-height: 2.75rem;
  font: inherit;
  cursor: pointer;
  touch-action: manipulation;
}

.brand {
  min-width: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: .45rem;
  border: 0;
  color: #f0e5d2;
  background: transparent;
  text-align: left;
}

.brand-mark {
  width: 1.9rem;
  height: 1.9rem;
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: 50%;
}

.brand-mark img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand>span:last-child {
  display: flex;
  flex-direction: column;
}

.brand strong {
  font-family: Georgia, serif;
  font-size: 1rem;
  letter-spacing: .04em;
  line-height: 1.05;
}

.brand small {
  display: none;
}

.icon-button {
  width: 2.75rem;
  padding: 0;
  display: grid;
  place-items: center;
  border: 1px solid rgba(196, 222, 207, .2);
  border-radius: 50%;
  color: #f0e5d2;
  background: #204445;
  font-size: 1.15rem;
}

.header-actions {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: .4rem;
}

.node-label {
  max-width: 5rem;
  overflow: hidden;
  color: #f3ba2f;
  font-size: .65rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lang-button {
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border: 1px solid rgba(196, 222, 207, .22);
  border-radius: 50%;
  color: #e1c299;
  background: rgba(196, 222, 207, .06);
  font-size: 1rem;
  padding: 0;
}

.lang-button img {
  width: 1.15rem;
  height: 1.15rem;
  object-fit: contain;
}

.wallet-button {
  max-width: 8.8rem;
  padding: 0 .7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .45rem;
  overflow: hidden;
  border: 1px solid rgba(211, 164, 119, .4);
  border-radius: 999px;
  color: #e5c096;
  background: rgba(211, 164, 119, .1);
  font-size: .75rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wallet-button i {
  width: .42rem;
  height: .42rem;
  flex: 0 0 auto;
  border-radius: 50%;
  background: #d9b48b;
  box-shadow: 0 0 .7rem rgba(217, 180, 139, .4);
}

.wallet-button.connected {
  color: #dfe8d9;
  border-color: rgba(145, 201, 174, .28);
  background: rgba(145, 201, 174, .08);
}

.wallet-button.connected i {
  background: #91c9ae;
  box-shadow: 0 0 .7rem rgba(145, 201, 174, .45);
}

@media (max-width: 22rem) {

  .brand small,
  .node-label {
    display: none;
  }

  .lang-button {
    width: 2.75rem;
    height: 2.75rem;
  }

  .wallet-button {
    max-width: 8.2rem;
  }
}
</style>
